import { TWELVE_OUT_BEAT, TWELVE_OUT_CUES, TWELVE_OUT_FLAGS, TWELVE_OUT_IDS } from '@/story/act2/twelveOut';
import { CULVERT_ENTER_X, HEDGEROW_NIGHT_SPAWN, HERD_HUDDLE_AT, PAST_FIELD_FENCE_Z, hedgerowBay, hedgerowGroundY } from '@/world/hedgerow';
import { disposeAsset } from '@/models/materials';
import { HedgerowNight } from '@/game/hedgerowNight';
import type { BeatPartCapabilities, BeatPartModule, BeatPartModuleDefinition } from '@/game/beatPartModule';
import { BeatSequencer } from '@/story/sequencer';

/** How close she must be to take the count; `wayGoals.ts` stops the look there too. */
export const HUDDLE_OBSERVE_REACH = 2.4;

/**
 * Two of beat 10's three facts are planes. Pure, and exported so the sim lane
 * can test them: the module itself builds a DOM overlay (the dog's fade) and
 * only boots in a browser.
 */
export function raiseTwelveOutPlanes(player: { readonly x: number; readonly z: number }, raiseFlag: (flag: string) => void): void {
  if (player.z < PAST_FIELD_FENCE_Z) raiseFlag(TWELVE_OUT_FLAGS.inTheField);
  if (player.x < CULVERT_ENTER_X) raiseFlag(TWELVE_OUT_FLAGS.inThePipe);
}

/**
 * Beat 10, *Twelve Out*, as a part module: the story, the planes, the fade,
 * and the night field (`HedgerowNight` — Holly, the dog, the queue, their
 * sounds) that `main.ts` used to construct and step inline (session 79).
 *
 * **The field's step straddles `physics.step`, and each half has its hook.**
 * The companion and her bell's stealth emission run in `beforePhysics`; the
 * dog's watch and the queue run in `afterPhysics`, because the watch is a
 * Rapier ray. Between those two, a capture puts her back at the night spawn
 * through `moveSelf`, and the queue then reads the spawn rather than the
 * scratch `player` — which is from before the placement. Every call sits where
 * the inline block sat; `HedgerowNight`'s header lists them.
 *
 * The field is built at `create`, not in the room: `?room=hedgerow-night` with
 * no beat is the census and shot-run control, and must stay an empty field.
 */
function createTwelveOutPartModule(capabilities: BeatPartCapabilities): BeatPartModule {
  const world = capabilities.world;
  if (world === undefined) throw new Error('beat 10 / part 1: the host offered no world capability');
  // **Beat 10 opens on a hole she already made** (D-173). Beat 7 broke this bay
  // and beat 10 is a later night in the same field, so the part breaks it
  // before the first frame: the room builds it whole for every boot, which is
  // what keeps `?room=hedgerow-night` with no beat — the census and shot-run
  // control — the whole-bay field it has always been (D-223). The mesh and the
  // collider go together: a broken bay with its collider standing is a wall
  // you can see through, the failure D-173 records the other way round.
  // `beat-10-gap.spec.ts` holds the delta.
  const seam = world.room.hedgerowBoards;
  if (seam === undefined) throw new Error('beat 10 / part 1: the room offers no boards seam — booted outside the hedgerow');
  seam.boards.removeFromParent();
  disposeAsset(seam.boards);
  seam.removeBoardsCollider();
  const brokenBay = hedgerowBay('broken');
  world.scene.add(brokenBay);
  const story = new BeatSequencer(TWELVE_OUT_BEAT);
  const fade = capabilities.createFade(TWELVE_OUT_CUES);
  const field = new HedgerowNight(world);
  let disposed = false;
  return {
    story,
    // ── The headcount, and it is the beat's only interactable ─────────────
    //
    // Spec §7.10 asks for the number to be *"visible only as a diegetic huddle
    // you can look at"*, and beat 8's hens established the rule the hard way:
    // the lesson is delivered by the shape of the event, not by a number. So
    // this is `observe` — three beats old by now — and its completion writes a
    // route mark on her map and **nothing else**. There is no counter and no
    // "N of 12"; the player takes the count themselves, once, and carries it.
    //
    // **Reach 2.4 (`HUDDLE_OBSERVE_REACH`), and `observeReachSlack` is a MULTIPLIER.** `interaction.ts` is
    // `distance > interactable.reach * slack`, so 2.4 x 3.0 is **7.2 m**, and that
    // number came off frames rather than off an argument. The ladder in
    // `docs/verification/beat10/ladder/` walks the counting shot in from 14 m: at
    // 10.4 m — the moment she is through the gap — the twelve are a pale mass and
    // the right-hand half of them is lost against the near hedge; at 7 m they
    // separate into countable bodies; at 4 m they are unmistakable. So the verb
    // offers itself at the distance where the thing it is for can actually be
    // done, and she walks 3.2 m of field to get there.
    //
    // **It was 4, against a 14.12 m spawn-to-huddle line that no longer exists**
    // (D-173): that line ran through the field fence, and the reach was set to
    // put the prompt just out of reach of a player who could not have got there
    // anyway. The intent it was written for — *"a thing you went and looked at,
    // not a thing the game showed you"* — survives the move only because the
    // number moved with it.
    //
    // **This was `reach: 9` for one commit, with a comment saying the slack
    // added 3.** It multiplies. The huddle was live from 27 m — a candidate on
    // the boot frame, from behind her spawn — and the entire "go and look" design
    // was absent while every gate stayed green, because nothing gates a reach.
    // An independent review caught it. Beat 3's Otto has the sibling comment and
    // gets it right: *"7 m, which `observeReachSlack` turns into 21"*.
    //
    // The duration is Otto's, and for Otto's reason: `focus` is the reward for
    // holding it (D-066), and what it buys here is the rig composing on twelve
    // animals so they resolve as twelve.
    //
    // `y` aims the snout at a head rather than at the grass in front of it. It is
    // NOT `FINISHER_SCALE.height`: 0.62 is the animal's BACK, which is what
    // `HERD_LEG.length` is derived to produce. The head centre sits at `HEAD.cy`
    // 0.30 plus the ~0.11 m `seatOnOrigin` lift, so about 0.41. It steers the
    // snout and nothing else — `#candidate` measures reach with `hypot(dx, dz)`.
    //
    // (Moved from `main.ts`'s never-reached legacy branch, session 77.)
    interactables: [{
      id: TWELVE_OUT_IDS.huddle,
      at: { x: HERD_HUDDLE_AT.x, y: hedgerowGroundY(HERD_HUDDLE_AT.x, HERD_HUDDLE_AT.z) + 0.41, z: HERD_HUDDLE_AT.z },
      verbs: ['observe'],
      reach: HUDDLE_OBSERVE_REACH,
      duration: 3.4,
      // One point, which drew nothing until the glyph (D-239): a route needs two.
      mark: { kind: 'route', at: [{ x: HERD_HUDDLE_AT.x, z: HERD_HUDDLE_AT.z }], glyph: 'pig' },
    }],
    get fade(): number { return fade.fade; },
    // `> 0.5` is the shared rule and it is one beat too late here: the prompt
    // has to be gone on the frame she crosses the plane (`HedgerowNight.closing`).
    get promptSuppressed(): boolean { return fade.fade > 0.5 || field.closing; },
    stealth: field.stealth,
    get callVerb(): 'call' | 'send' { return field.callVerb; },
    // Beat 10's three facts: two planes and the queue's `theyAreMoving`.
    // `beat-10-close.spec.ts` drives her across each plane rather than adding
    // the flag by hand. `inTheField` is the plane beat 7 calls `pastTheFence`
    // — the hole she made there, which D-173 is why beat 10 opens on;
    // `CULVERT_ENTER_X` carries its own derivation (a body length inside the bore).
    fixedStep({ player, raiseFlag }): void {
      raiseTwelveOutPlanes(player, raiseFlag);
      // The queue's fact, before the story reads flags this tick (one tick
      // stale by design; `HedgerowNight.addStoryFlags` says why).
      field.addStoryFlags(raiseFlag);
    },
    beforePhysics({ dt, player, called }): void {
      if (called === true) field.toggleCall();
      field.stepCompanion(dt, player);
    },
    afterPhysics({ dt, player, moveSelf }): void {
      if (moveSelf === undefined) throw new Error('beat 10 / part 1: the tick must carry moveSelf');
      // A capture is the host's body to move; the queue must then read the
      // pose she was put at, not the scratch copy from before the call.
      let at: { x: number; y: number; z: number } = player;
      if (field.stepWatch(dt, player)) {
        moveSelf(HEDGEROW_NIGHT_SPAWN);
        at = HEDGEROW_NIGHT_SPAWN;
      }
      field.stepQueue(dt, at);
    },
    handleCue(cue): void {
      field.handleCue(cue);
      fade.handle(cue);
    },
    renderStep(frameDt): void {
      if (disposed) return;
      fade.update(frameDt);
      field.renderWorld(frameDt);
    },
    renderLate(frameDt): void { if (!disposed) field.renderLate(frameDt); },
    // Everything but the dog, which is deliberate (`HedgerowNight.resetForRespawn`).
    resetForRespawn(): void { field.resetForRespawn(); },
    outcome: () => ({ out: field.out }),
    diagnostics: (player) => ({ kind: 'twelve-out', twelveOut: field.debug(player) }),
    dispose(): void {
      if (disposed) return;
      disposed = true;
      // The field owns its loops (the herd bed, the pant) and stops them here.
      field.dispose();
      fade.dispose();
      world.scene.remove(brokenBay);
    },
  };
}

export const TWELVE_OUT_PART_MODULE: BeatPartModuleDefinition = {
  beat: 10,
  part: 1,
  room: 'hedgerow-night',
  create: createTwelveOutPartModule,
};
